import React, { useMemo, useState, Children } from 'react';
import { QuotesPageV2, QuotesContext, dummyQuotes } from './QuotesPageV2';
import { ChevronRightIcon, ChevronDownIcon, GlobeIcon, BuildingIcon, MapPinIcon, CheckIcon, SearchIcon } from 'lucide-react';
// --- Types & Mock Data from TerritoryManagement ---
type NodeType = 'region' | 'subregion' | 'country' | 'dealer';
interface AccessEntry {
  userId: string;
  inheritedFrom?: string;
}
interface TerritoryNode {
  id: string;
  type: NodeType;
  name: string;
  children?: TerritoryNode[];
  accessUsers: AccessEntry[];
  defaultQuoteResponsible?: string | 'mixed' | 'unassigned';
}
const INITIAL_TERRITORY: TerritoryNode[] = [{
  id: 'r1',
  type: 'region',
  name: 'Aisco EUROPE',
  accessUsers: [{
    userId: 'u3'
  }],
  defaultQuoteResponsible: 'mixed',
  children: [{
    id: 'sr1',
    type: 'subregion',
    name: 'R3 Sasha',
    accessUsers: [{
      userId: 'u1'
    }, {
      userId: 'u3',
      inheritedFrom: 'Aisco EUROPE'
    }],
    defaultQuoteResponsible: 'mixed',
    children: [{
      id: 'c1',
      type: 'country',
      name: 'Moldova',
      accessUsers: [{
        userId: 'u1',
        inheritedFrom: 'R3 Sasha'
      }, {
        userId: 'u3',
        inheritedFrom: 'Aisco EUROPE'
      }],
      defaultQuoteResponsible: 'u1',
      children: [{
        id: 'd1',
        type: 'dealer',
        name: 'Dealer Moldova 1',
        accessUsers: [{
          userId: 'u1',
          inheritedFrom: 'R3 Sasha'
        }, {
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'u1'
      }, {
        id: 'd2',
        type: 'dealer',
        name: 'Dealer Moldova 2',
        accessUsers: [{
          userId: 'u1',
          inheritedFrom: 'R3 Sasha'
        }, {
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'u1'
      }]
    }, {
      id: 'c2',
      type: 'country',
      name: 'Ukraine',
      accessUsers: [{
        userId: 'u1',
        inheritedFrom: 'R3 Sasha'
      }, {
        userId: 'u2'
      }, {
        userId: 'u3',
        inheritedFrom: 'Aisco EUROPE'
      }],
      defaultQuoteResponsible: 'mixed',
      children: [{
        id: 'd3',
        type: 'dealer',
        name: 'DAIRY GLOBAL EXPERTS LLC LN',
        accessUsers: [{
          userId: 'u1',
          inheritedFrom: 'R3 Sasha'
        }, {
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'u1'
      }, {
        id: 'd4',
        type: 'dealer',
        name: 'Agro Dealer Ukraine 2',
        accessUsers: [{
          userId: 'u1',
          inheritedFrom: 'R3 Sasha'
        }, {
          userId: 'u2',
          inheritedFrom: 'Ukraine'
        }, {
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'u2'
      }, {
        id: 'd5',
        type: 'dealer',
        name: 'Unassigned Dealer',
        accessUsers: [{
          userId: 'u3',
          inheritedFrom: 'Aisco EUROPE'
        }],
        defaultQuoteResponsible: 'unassigned'
      }]
    }]
  }]
}];
function getDescendantIds(node: TerritoryNode): string[] {
  let ids: string[] = [];
  if (node.children) {
    for (const child of node.children) {
      ids.push(child.id);
      ids = ids.concat(getDescendantIds(child));
    }
  }
  return ids;
}
function collectDealers(node: TerritoryNode): TerritoryNode[] {
  if (node.type === 'dealer') return [node];
  let dealers: TerritoryNode[] = [];
  if (node.children) {
    for (const child of node.children) dealers = dealers.concat(collectDealers(child));
  }
  return dealers;
}
function findNodeById(nodes: TerritoryNode[], id: string): TerritoryNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.children) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}
export function QuotesPageV3() {
  const [territory] = useState<TerritoryNode[]>(INITIAL_TERRITORY);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set(['r1', 'sr1', 'c1', 'c2']));
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  // --- Filtering ---
  const filterTree = (nodes: TerritoryNode[]): TerritoryNode[] => {
    if (!searchQuery) return nodes;
    const query = searchQuery.toLowerCase();
    return nodes.map(node => {
      const matchesSearch = node.name.toLowerCase().includes(query);
      let filteredChildren: TerritoryNode[] | undefined;
      if (node.children) filteredChildren = filterTree(node.children);
      const hasMatchingChildren = filteredChildren && filteredChildren.length > 0;
      if (matchesSearch || hasMatchingChildren) {
        return {
          ...node,
          children: filteredChildren
        };
      }
      return null;
    }).filter(Boolean) as TerritoryNode[];
  };
  const filteredTerritory = useMemo(() => filterTree(territory), [territory, searchQuery]);
  const flattenTree = (nodes: TerritoryNode[], level = 0, parentVisible = true): any[] => {
    let result: any[] = [];
    for (const node of nodes) {
      const isExpanded = expandedNodes.has(node.id);
      if (parentVisible) result.push({
        ...node,
        level
      });
      if (node.children) {
        result = result.concat(flattenTree(node.children, level + 1, parentVisible && isExpanded));
      }
    }
    return result;
  };
  const flatNodes = useMemo(() => flattenTree(filteredTerritory), [filteredTerritory, expandedNodes]);
  const toggleExpand = (id: string) => {
    const next = new Set(expandedNodes);
    next.has(id) ? next.delete(id) : next.add(id);
    setExpandedNodes(next);
  };
  const toggleSelect = (node: TerritoryNode) => {
    const next = new Set(selectedNodes);
    const isSelected = next.has(node.id);
    const descendantIds = getDescendantIds(node);
    if (isSelected) {
      next.delete(node.id);
      descendantIds.forEach(id => next.delete(id));
    } else {
      next.add(node.id);
      descendantIds.forEach(id => next.add(id));
    }
    setSelectedNodes(next);
  };
  const isIndeterminate = (node: TerritoryNode) => {
    if (!node.children || node.children.length === 0) return false;
    const descendantIds = getDescendantIds(node);
    const selectedCount = descendantIds.filter(id => selectedNodes.has(id)).length;
    return selectedCount > 0 && selectedCount < descendantIds.length;
  };
  const getNodeIcon = (type: NodeType) => {
    switch (type) {
      case 'region':
        return <GlobeIcon size={14} className="text-blue-400" data-id="element-12733" data-mp="1hoonx0q3ibji" />;
      case 'subregion':
        return <MapPinIcon size={14} className="text-indigo-400" data-id="element-12734" data-mp="1mkwxlylixcja" />;
      case 'country':
        return <MapPinIcon size={14} className="text-teal-400" data-id="element-12735" data-mp="1rmcmy2uqi57h" />;
      case 'dealer':
        return <BuildingIcon size={14} className="text-amber-400" data-id="element-12736" data-mp="1vn68ipkpf7x7" />;
    }
  };
  // --- Filter Quotes based on selected tree nodes ---
  const filteredQuotes = useMemo(() => {
    if (selectedNodes.size === 0) return dummyQuotes; // If nothing selected, show all
    const selectedDealers = new Set<string>();
    selectedNodes.forEach(id => {
      const node = findNodeById(territory, id);
      if (node) {
        collectDealers(node).forEach(d => selectedDealers.add(d.name));
      }
    });
    return dummyQuotes.filter(q => selectedDealers.has(q.dealer));
  }, [selectedNodes, territory]);
  return <div className="flex h-screen overflow-hidden bg-gradient-to-br from-slate-900 via-blue-950 to-indigo-950" data-id="element-12737" data-mp="3uuczqf89aazi">
      {/* Left Sidebar - Tree View */}
      <aside className="w-[320px] bg-slate-900/95 border-r border-white/10 flex flex-col flex-shrink-0 z-20 shadow-2xl" data-id="element-12738" data-mp="3l3i9z0vswt9b">
        <div className="p-4 border-b border-white/10 bg-black/20" data-id="element-12739" data-mp="me0anaa5znh3">
          <h2 className="text-lg font-bold text-white mb-2" data-id="element-12740" data-mp="mfbdn7vscr75">Scope</h2>
          <div className="relative" data-id="element-12741" data-mp="hl75wmep4f50">
            <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-300/50" data-id="element-12742" data-mp="3m4l3nys3lc23" />
            <input type="text" placeholder="Search territory..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-4 py-1.5 text-sm text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-12743" data-mp="3qv1h3ivx4xog" />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 no-scrollbar" data-id="element-12744" data-mp="8qzwet8uykif">
          {flatNodes.map(node => {
          const hasChildren = node.children && node.children.length > 0;
          const isExpanded = expandedNodes.has(node.id);
          const isSelected = selectedNodes.has(node.id);
          const indeterminate = isIndeterminate(node);
          return <div key={node.id} className="flex items-center py-1.5 px-2 hover:bg-white/5 rounded-lg transition-colors cursor-pointer" style={{
            paddingLeft: `${node.level * 16 + 8}px`
          }} data-id="element-12745" data-mp="2kf8xqpng9o2o">
                <div className={`w-4 h-4 rounded border flex items-center justify-center cursor-pointer transition-colors mr-2 flex-shrink-0 ${isSelected ? 'bg-blue-500 border-blue-500' : indeterminate ? 'bg-blue-500/50 border-blue-500' : 'border-white/30 hover:border-white/50 bg-black/20'}`} onClick={() => toggleSelect(node)} data-id="element-12746" data-mp="2gx1s9qjt2plx">
                  {isSelected && <CheckIcon size={12} className="text-white" data-id="element-12747" data-mp="2lwxvvoolcf0y" />}
                  {!isSelected && indeterminate && <div className="w-2 h-0.5 bg-white rounded-full" data-id="element-12748" data-mp="1y7dakz4yt5oi" />}
                </div>

                <div className={`w-5 h-5 flex items-center justify-center cursor-pointer text-blue-300 hover:text-white flex-shrink-0 ${!hasChildren ? 'invisible' : ''}`} onClick={() => hasChildren && toggleExpand(node.id)} data-id="element-12749" data-mp="1t91zkl2xop4p">
                  {isExpanded ? <ChevronDownIcon size={14} data-id="element-12750" data-mp="2343nxepdxn68" /> : <ChevronRightIcon size={14} data-id="element-12751" data-mp="1ffssir2gkfpc" />}
                </div>

                <div className="w-5 flex justify-center flex-shrink-0 mr-1" data-id="element-12752" data-mp="1fdzq5nyvohn5">
                  {getNodeIcon(node.type)}
                </div>

                <span className={`text-sm truncate ${node.type === 'dealer' ? 'text-white' : 'text-blue-100'}`} data-id="element-12753" data-mp="331z8y8japnso">
                  {node.name}
                </span>
              </div>;
        })}
        </div>
      </aside>

      {/* Main Content - QuotesPageV2 */}
      <main className="flex-1 relative overflow-hidden" data-id="element-12754" data-mp="2o5rqpdrg6wg1">
        <QuotesContext.Provider value={filteredQuotes} data-id="element-12755" data-mp="2o585no6i3rxs">
          <QuotesPageV2 data-id="element-12756" data-mp="2k4od6xwvhchz" />
        </QuotesContext.Provider>
      </main>
    </div>;
}